import * as t from 'io-ts'
import { MultiLocaleString, emptyMultiLocaleString } from './multi-locale-string'
import { TTrend } from './trend'
import { Projection, Result } from './schema'
import { PopularVoteVisibility } from './popular-vote-visibility'
import * as firestore from 'firebase/firestore'

export const isTimestamp = (u: unknown): u is firestore.Timestamp => {
  /*
   * We used an instanceof check (u instanceof firebase.firestore.Timestamp), but
   * that does not work since this function is also used in the Firebase functions
   * which provide a different implementation of the Timestamp class so the
   * instanceof check won't work.
   */

  if (typeof u === 'object' && u !== null) {
    return 'seconds' in u && 'nanoseconds' in u && 'toDate' in u
  } else {
    return false
  }
}

export const Timestamp = new t.Type<firestore.Timestamp>(
  'Timestamp',
  isTimestamp,
  (u, c) => (isTimestamp(u) ? t.success(u) : t.failure(u, c)),
  t.identity,
)

export const TobiIntegration = t.type({
  enabled: t.boolean,
  articleUrlTemplate: t.union([t.null, t.string]),
})
export type TobiIntegration = t.TypeOf<typeof TobiIntegration>

export const AutoImport = t.type({
  enabled: t.union([t.undefined, t.boolean]),
  at: t.union([t.undefined, t.null, Timestamp]),
})
export type AutoImport = t.TypeOf<typeof AutoImport>

export const Status = t.union([
  t.null,
  t.undefined,
  t.literal('trend'),
  t.literal('projection'),
  t.literal('result'),
  t.literal('empty'),
])

export const ElectionDocument = t.type({
  articleIds: MultiLocaleString,
  data: t.type({
    nr: t.type({
      display: t.array(
        t.type({
          partyId: t.union([t.null, t.undefined, t.string]),
          lastOutcome: t.type({
            value: t.union([t.null, t.undefined, t.number]),
            seats: t.union([t.null, t.undefined, t.number]),
          }),
          percent: t.type({
            value: t.union([t.null, t.undefined, t.number]),
            difference: t.union([t.null, t.undefined, t.number]),
          }),
          percentOverride: t.type({
            value: t.union([t.null, t.undefined, t.number]),
            difference: t.union([t.null, t.undefined, t.number]),
          }),
          seats: t.type({
            value: t.union([t.null, t.undefined, t.number]),
            difference: t.union([t.null, t.undefined, t.number]),
          }),
          seatsOverride: t.type({
            value: t.union([t.null, t.undefined, t.number]),
            difference: t.union([t.null, t.undefined, t.number]),
          }),
        }),
      ),
      meta: t.type({
        label: MultiLocaleString,
        status: Status,
      }),
    }),
    sr: t.type({
      display: t.array(t.undefined),
      meta: t.type({
        fst: t.type({
          label: MultiLocaleString,
          majority: t.union([t.null, t.undefined, t.number]), //is this really a number ????
          status: Status,
        }),
        snd: t.type({
          label: MultiLocaleString,
          majority: t.union([t.null, t.undefined, t.number]), //is this really a number ????
          status: Status,
        }),
      }),
    }),
    voterTurnout: t.union([t.null, t.undefined, t.number]),
    voterTurnoutOverride: t.union([t.null, t.undefined, t.number]),
  }),
  text: t.type({
    nr: t.type({
      seats: t.type({
        heading: MultiLocaleString,
        status: MultiLocaleString,
        text: MultiLocaleString,
      }),
      top: t.type({
        heading: MultiLocaleString,
        text: MultiLocaleString,
      }),
      trend: t.type({
        heading: MultiLocaleString,
        text: MultiLocaleString,
      }),
      voterTurnout: t.type({
        heading: MultiLocaleString,
        text: MultiLocaleString,
      }),
    }),
    sr: t.type({
      text: MultiLocaleString,
    }),
  }),
  title: MultiLocaleString,
  toby: t.union([t.undefined, TobiIntegration]),
  parties: t.any,
})

export type ElectionDocument = t.TypeOf<typeof ElectionDocument>

export const FederalPartiesDocument = t.type({
  abbr: MultiLocaleString,
  color: t.union([t.null, t.undefined, t.string]),
  name: MultiLocaleString,
  orientation: t.union([t.null, t.undefined, t.number]),
  sr: t.type({
    seatsPrevious: t.union([t.null, t.undefined, t.number]),
  }),
  staticIndex: t.union([t.null, t.undefined, t.number]),
})

export type FederalPartiesDocument = t.TypeOf<typeof FederalPartiesDocument>

export const PopularVoteDocument = t.type({
  day: Timestamp,

  autoImport: t.union([t.undefined, AutoImport]),

  dashboardArticleId: t.union([t.undefined, MultiLocaleString]),

  name: MultiLocaleString,
  lead: MultiLocaleString,

  articleIds: MultiLocaleString,
  contentBlocks: MultiLocaleString,

  visibility: PopularVoteVisibility,

  tobiIntegration: t.union([t.undefined, TobiIntegration]),
})
export type PopularVoteDocument = t.TypeOf<typeof PopularVoteDocument>

/**
 * A document which contains everything that we need to derive 'BallotState'.
 *
 * @see docToBallotState
 */
export const BallotStateDocument = t.partial({
  trend: TTrend,
  projection: Projection,
  result: Result,
})
export type BallotStateDocument = t.TypeOf<typeof BallotStateDocument>

export const BallotGroup = t.type({
  initiativeId: t.string,
  counterProposalId: t.string,
  tiebreakQuestionId: t.string,
})
export type BallotGroup = t.TypeOf<typeof BallotGroup>

export const SideLabels = t.type({
  yea: MultiLocaleString,
  nay: MultiLocaleString,
})
export type SideLabels = t.TypeOf<typeof SideLabels>

export const emptySideLabels: SideLabels = {
  yea: emptyMultiLocaleString,
  nay: emptyMultiLocaleString,
}

/**
 * This is the shape that is common to all documents stored in the
 * '/popularVotes/<id>/ballots' collection.
 */
export const BallotDocumentBase = t.intersection([
  BallotStateDocument,
  t.type({
    position: t.number,

    name: MultiLocaleString,
    lead: MultiLocaleString,

    /**
     * The name used on the ballot detail page. It is optional, if not provided
     * then the name is used.
     *
     * TODO: Make this field required.
     */
    detailName: t.union([t.undefined, MultiLocaleString]),

    /**
     * Not a string that we show in the UI, but a comma-separated list of article IDs.
     * In the backoffice it's edited as a string, but in the frontend we parse this into
     * a list of article IDs.
     */
    articleIds: MultiLocaleString,

    /**
     * When the ballot is part of a group (popular initiative / counter proposal /
     * tiebreak question), these IDs point to the others in the group.
     */
    group: t.union([t.undefined, t.null, BallotGroup]),

    /**
     * Labels for the yea/nay sides. This is optional, if not provided then the defaults
     * Ja / Nein are used for regular ballots and Initiative / Gegenvorschlag for
     * tiebreak questions.
     */
    sideLabels: t.union([t.undefined, t.null, SideLabels]),
  }),
])
export type BallotDocumentBase = t.TypeOf<typeof BallotDocumentBase>

export function isTiebreakQuestion(id: string, doc: Pick<BallotDocumentBase, 'group'>): boolean {
  return doc.group ? doc.group.tiebreakQuestionId === id : false
}

export const FederalBallotDocument = t.intersection([
  BallotDocumentBase,
  t.type({
    majorityOfCantonsRequired: t.union([t.undefined, t.boolean]),
    externalSource: t.type({
      vorlagenId: t.union([t.null, t.Int]),
      name: MultiLocaleString,
    }),
  }),
])
export type FederalBallotDocument = t.TypeOf<typeof FederalBallotDocument>

export const CantonalBallotDocument = t.intersection([
  BallotDocumentBase,
  t.type({
    externalSource: t.type({
      vorlagenId: t.union([t.null, t.Int]),
      name: MultiLocaleString,
    }),
  }),
])
export type CantonalBallotDocument = t.TypeOf<typeof CantonalBallotDocument>
